// SHCT-828 — the single R지수 pill: `R 0.6 · 저위험`, tinted by the risk level.
//
// The home dashboard's detail panels each spelled this out themselves and landed
// on a different presentation from 환자 관리: the R value went into a neutral /red
// `grade-chip--r` and the 위험도 sat next to it in its own `r-badge`. A 저위험
// patient therefore showed a red-looking R chip beside a green 저위험 badge, and
// the same patient two screens over showed one green pill. PRD 02 §4.2 fixes the
// `R 0.0` number format but says nothing about colour or shape, so the ticket
// names 환자 관리 as the reference — this component is that rendering, extracted.
//
// States mirror `PatientDetailHeader` (PRD 11 §7.2 / PD-01 §3.1):
//   · 산출 불가 — riskLevel UNKNOWN → red pill reading 산출 불가 (no number)
//   · scored    — `R x.x · 저/중/고위험`, tinted by level
//   · neither   — grey `R —`
// Keyed off riskLevel, never `riskScore == null`: a patient who has simply not
// submitted yet also has a null score but is NOT 산출 불가.

import { PatientStatus, RiskLevel, isUnderManagement } from "@/api/adapters/patients";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

interface RiskPillProps {
	riskScore: number | null | undefined;
	riskLevel: RiskLevel | string | null | undefined;
	t: Translate;
	/**
	 * Which pill skin to wear. The wording, the level mapping and the states are
	 * the same everywhere — only the box differs:
	 *   · "panel" (default) — `r-pill`, the 12px chip the home detail panels use.
	 *   · "card"            — `patient-card__r-pill`, the 14px chip sized for the
	 *                         narrow 환자 목록 card, which sits at 14px next to the
	 *                         card's name row and is right-aligned by margin-left.
	 * Both skins read the same --risk-* tokens, so the colours cannot drift.
	 */
	variant?: "panel" | "card";
	/**
	 * FEATURE_037 — the patient's management status, when the surface lists patients of
	 * every status. A care-ended patient carries no live R, so anything the shared
	 * {@link isUnderManagement} helper does not call "still under care" reads grey `R —`
	 * whatever level is still stored. Omitted → no guard (surfaces that list ACTIVE
	 * patients only).
	 */
	status?: PatientStatus | null;
}

const LEVEL_MOD: Record<string, "low" | "mod" | "high"> = {
	[RiskLevel.G1]: "low",
	[RiskLevel.G2]: "mod",
	[RiskLevel.G3]: "high",
};

const LEVEL_LABEL_KEY: Record<string, string> = {
	[RiskLevel.G1]: "medical:patients.header.riskLevel.low",
	[RiskLevel.G2]: "medical:patients.header.riskLevel.moderate",
	[RiskLevel.G3]: "medical:patients.header.riskLevel.high",
};

export function RiskPill({
	riskScore,
	riskLevel,
	t,
	variant = "panel",
	status,
}: RiskPillProps) {
	const base = variant === "card" ? "patient-card__r-pill" : "r-pill";

	// Ask the one helper that owns the set, never re-spell it: `patients.ts` says a screen
	// that writes its own shape silently admits every status added later.
	if (status != null && !isUnderManagement(status)) {
		return <span className={`${base} ${base}--empty`}>R —</span>;
	}

	if (riskLevel === RiskLevel.UNKNOWN) {
		const label = t("medical:patients.header.rIndex.cannotCompute");
		return (
			<span className={`${base} ${base}--unknown`} title={label}>
				{label}
			</span>
		);
	}

	const mod = riskLevel ? LEVEL_MOD[riskLevel] : undefined;
	if (riskScore == null || !mod) {
		return <span className={`${base} ${base}--empty`}>R —</span>;
	}

	const levelLabel = t(LEVEL_LABEL_KEY[riskLevel as string]);
	return (
		<span
			className={`${base} ${base}--${mod}`}
			title={t("medical:patients.header.rIndex.rPillTooltip", { level: levelLabel })}
		>
			R {riskScore.toFixed(1)} · {levelLabel}
		</span>
	);
}
